import "src/analytics/dashboard/dashboardRglStyles.css";

import { GridLayout, type Layout, verticalCompactor } from "react-grid-layout";
import useMeasure from "react-use-measure";

import { DashboardBackgroundGrid } from "src/analytics/dashboard/DashboardBackgroundGrid";
import { DashboardPinChart } from "src/analytics/dashboard/DashboardPinChart";
import {
  GRID_COLUMNS,
  GRID_CONTAINER_PADDING,
  GRID_GAP,
  GRID_MARGIN,
  ROW_HEIGHT,
} from "src/analytics/dashboard/dashboardGridLayout";
import { StaticPinGrid } from "src/analytics/dashboard/StaticPinGrid";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import type { Pin, Realm } from "src/clients/analytics-dashboard";

// Empty grid rows kept below the last chart so there's room to drag into.
const BOTTOM_PADDING_ROWS = 3;

export const DashboardLayoutEditor: React.FC<{
  pins: Pin[];
  realm: Realm;
  realmId: string;
  labelDictionaries: LabelDictionaries;
  layout: Layout;
  onLayoutChange: (layout: Layout) => void;
  isSaving?: boolean;
  dashboardId?: string;
}> = ({
  pins,
  realm,
  realmId,
  labelDictionaries,
  layout,
  onLayoutChange,
  isSaving = false,
  dashboardId,
}) => {
  const [containerRef, { width }] = useMeasure();
  const occupiedRows = layout.reduce(
    (max, item) => Math.max(max, item.y + item.h),
    0,
  );
  const rows = occupiedRows + BOTTOM_PADDING_ROWS;

  return (
    <div
      className="dashboard-rgl decideScrollbar h-full overflow-y-auto"
      data-loc="dashboard-layout-editor"
    >
      <div
        ref={containerRef}
        className="relative isolate"
        style={
          width > 0
            ? { minHeight: rows * (ROW_HEIGHT + GRID_GAP) + GRID_GAP }
            : undefined
        }
      >
        {width > 0 && (
          <>
            <DashboardBackgroundGrid rows={rows} />
            <GridLayout
              compactor={verticalCompactor}
              dragConfig={{
                enabled: !isSaving,
                handle: ".dashboard-drag-handle",
              }}
              gridConfig={{
                cols: GRID_COLUMNS,
                rowHeight: ROW_HEIGHT,
                margin: GRID_MARGIN,
                containerPadding: GRID_CONTAINER_PADDING,
              }}
              layout={layout}
              resizeConfig={{ enabled: !isSaving, handles: ["se"] }}
              width={width}
              onLayoutChange={onLayoutChange}
            >
              {pins.map((pin) => (
                <div key={pin.id} className="h-full">
                  <DashboardPinChart
                    dashboardId={dashboardId}
                    isEditing
                    labelDictionaries={labelDictionaries}
                    pin={pin}
                    realm={realm}
                    realmId={realmId}
                  />
                </div>
              ))}
            </GridLayout>
          </>
        )}
      </div>
      {width === 0 && (
        <StaticPinGrid
          dashboardId={dashboardId}
          labelDictionaries={labelDictionaries}
          pins={pins}
          realm={realm}
          realmId={realmId}
        />
      )}
    </div>
  );
};
